import type { MetricsRow } from 'app/services/metrics'
import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { useState } from 'react'
import { LazyStats } from 'app/components/LazyStats'
import ProgressBar from 'app/components/ProgressBar'
import MonthlyLocationBreakdownTable from 'app/components/shared/MonthlyLocationBreakdownTable'
import { Stats } from 'app/components/Stats'
import EditableCell from 'app/components/table/EditableCell'
import MetricCell from 'app/components/table/MetricCell'
import { PMSIntegrationCell } from 'app/components/table/PMSIntegrationCell'
import { Tooltip } from 'app/components/Tooltip'
import TagsCell from 'app/components/tags/TagsCell'
import { formatDate } from 'app/date.utils'
import {
  FilterOperator,
  addDefaultFilterProperties,
  FilterValueType,
  FilterableColumn,
  PredefinedFilter,
} from 'app/filter.utils'
import type { ActivityJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { graphql } from 'app/generated/gql'
import { gqlMutation, gqlFetchList } from 'app/lib/graphqlClient'

const UPDATE_BUDGET_MUTATION = graphql(`
  mutation UpdateBudgetTable($id: ID!, $input: UpdateBudgetInput!) {
    updateBudget(id: $id, input: $input) {
      id
    }
  }
`)

const CREATE_BUDGET_MUTATION = graphql(`
  mutation CreateBudget($input: CreateBudgetInput!) {
    createBudget(input: $input) {
      id
    }
  }
`)
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { getActivityTypeColorMap, getLeadStatusColorMap } from 'lib/colorMappings'
import { logError } from 'lib/logging'

// The Locations table lazy-loads rows on scroll, so footer totals must NOT be derived from the
// rows currently in the table (that count grows as the user scrolls — TRK-5184). Instead we use
// the server-side `marketingMetrics.summary`, which aggregates the entire dataset. `summaryTotal`
// returns the summary value for a field when available, falling back to the loaded-rows sum only
// when the summary does not carry that field (e.g. before the summary has loaded).
type LocationsSummary = Partial<MetricsRow> | undefined

function summaryTotal(summary: LocationsSummary, field: string, rows: { original: MetricsRow }[]): number {
  const fromSummary = summary?.[field]
  if (typeof fromSummary === `number`) return fromSummary
  return rows.reduce((total, row) => total + (Number(row.original[field]) || 0), 0)
}

export type MetricsRowPatcher = (row: MetricsRow, patch: Partial<MetricsRow>) => void

export const defaultColumns = (
  openLocationsTotals: (locationIds: string[]) => void,
  showSensitiveColumns: boolean,
  isSMC: boolean,
  fromDate?: string,
  toDate?: string,
  channel?: string,
  summary?: LocationsSummary,
  onDetailRowPatch?: MetricsRowPatcher,
  getDetailRowPatch?: (locationId: string, row: MetricsRow) => Partial<MetricsRow>,
): ColumnDef<MetricsRow, any>[] => [
  {
    accessorFn: (row) => row.location?.name,
    header: `Location Name`,
    footer: (props) => (
      <button
        className="hover:font-bold hover:underline"
        onClick={() => openLocationsTotals(props.table.getRowModel().rows?.map((row) => row.original.bucket))}
      >
        Total
      </button>
    ),
    id: `Location Name`,
    meta: {
      defaultVisible: true,
      pinned: true,
      onClick: (row) =>
        showModal(
          <MonthlyLocationBreakdownTable
            locationIds={[row.locationId!]}
            columns={(patchModalRow) =>
              detailColumns(showSensitiveColumns, isSMC, row.locationId!, (editedRow, patch) => {
                patchModalRow(editedRow, patch)
                onDetailRowPatch?.({ ...editedRow, locationId: editedRow.locationId || row.locationId }, patch)
              })
            }
            channel="Digital Marketing"
            queryParams={[`showDisqualified=false`]}
            getRowPatch={(detailRow) => getDetailRowPatch?.(row.locationId!, detailRow) ?? {}}
          />,
          {
            title: row.location?.name || ``,
            subtitle: row.workspace?.name || ``,
            isFullScreen: false,
            is80Percent: true,
            useRadix: true,
          },
        ),
    },
    enableHiding: false,
    sortingFn: `text`,
  },
  {
    accessorFn: (row) => row.workspace?.name,
    id: `Workspace`,
    header: `Workspace`,
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.location?.pmsIntegrated,
    id: `PMS Integration`,
    header: `PMS Integration`,
    cell: (props) => (
      <PMSIntegrationCell
        isIntegrated={props.getValue()}
        lastPmsSyncDate={props.row.original.location?.lastPmsSyncDate}
      />
    ),
    meta: {
      defaultVisible: true,
    },
    sortUndefined: -1,
  },
  {
    accessorFn: (row) => row.budget,
    id: `Budget`,
    header: `Budget`,
    cell: (props) => <MetricCell value={props.row.original.budget} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `budget`, table.getFilteredRowModel().rows)} type="dollars" />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.location?.orgTags,
    id: `Org Tags`,
    header: `Org Tags`,
    meta: {
      defaultVisible: false,
      ControlledComponent: (row) => (
        <TagsCell tags={row?.location?.orgTags} model="locations" modelId={row?.locationId!} isOrgTags />
      ),
    },
    enableSorting: false,
  },
  ...(showSensitiveColumns
    ? ([
        {
          accessorFn: (row) => row.facebookSpend,
          id: `Facebook Spend`,
          header: `Facebook Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={summaryTotal(summary, `facebookSpend`, table.getFilteredRowModel().rows)}
              type="dollars"
            />
          ),
        },
        {
          accessorFn: (row) => row.googleSpend,
          id: `Google Spend`,
          header: `Google Spend`,
          cell: ({ row }) => {
            let lsa = row.original.lsaSpend || 0
            let total = row.original.googleSpend || 0
            let googleOnly = total - lsa
            if (lsa > 0) {
              return (
                <Tooltip
                  message={
                    <div className="text-xs">
                      <div>
                        Google Ads: $
                        {googleOnly.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                      <div>
                        Local Service Ads: $
                        {lsa.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  }
                >
                  <MetricCell value={total} type="dollars" />
                </Tooltip>
              )
            }
            return <MetricCell value={total} type="dollars" />
          },
          footer: ({ table }) => (
            <MetricCell value={summaryTotal(summary, `googleSpend`, table.getFilteredRowModel().rows)} type="dollars" />
          ),
        },
        {
          accessorFn: (row) => (row.facebookSpend || 0) + (row.googleSpend || 0),
          id: `Total Spend`,
          header: `Total Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={
                summaryTotal(summary, `facebookSpend`, table.getFilteredRowModel().rows) +
                summaryTotal(summary, `googleSpend`, table.getFilteredRowModel().rows)
              }
              type="dollars"
            />
          ),
        },
        {
          accessorFn: (row) => row.targetSpend,
          id: `Target Spend`,
          header: `Target Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell value={summaryTotal(summary, `targetSpend`, table.getFilteredRowModel().rows)} type="dollars" />
          ),
        },
        {
          id: `iCPL`,
          header: `iCPL`,
          accessorFn: (row) => row.iCPL,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={
                (summaryTotal(summary, `facebookSpend`, table.getFilteredRowModel().rows) +
                  summaryTotal(summary, `googleSpend`, table.getFilteredRowModel().rows)) /
                summaryTotal(summary, `leads`, table.getFilteredRowModel().rows)
              }
              type="dollars"
            />
          ),

          meta: {},
        },
        {
          id: `iCPQL`,
          header: `iCPQL`,
          accessorFn: (row) => row.iCPQL,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={
                (summaryTotal(summary, `facebookSpend`, table.getFilteredRowModel().rows) +
                  summaryTotal(summary, `googleSpend`, table.getFilteredRowModel().rows)) /
                summaryTotal(summary, `MQLs`, table.getFilteredRowModel().rows)
              }
              type="dollars"
            />
          ),

          meta: {},
        },
        {
          id: `Facebook Ads`,
          header: `Facebook Ads`,
          cell: (props) => (
            <div className="flex">
              <img
                className={classNames(
                  `mx-1 h-6 w-auto`,
                  typeof props.row.original.location?.facebookAdsActive === `undefined`
                    ? `opacity-0`
                    : props.row.original.location?.facebookAdsActive
                      ? `grayscale-0`
                      : `grayscale`,
                )}
                src="/public/fb.png"
                alt="Facebook Ads"
              />
            </div>
          ),
          meta: {
            defaultVisible: false,
          },
          enableSorting: false,
        },
        {
          id: `Google Ads`,
          header: `Google Ads`,
          cell: (props) => (
            <div className="flex">
              <img
                className={classNames(
                  `h-6 w-auto`,
                  !props.row.original.location?.googleAdsV3
                    ? `opacity-0`
                    : props.row.original.location?.googleAdsActive
                      ? `grayscale-0`
                      : `grayscale`,
                )}
                src="/public/google_ads.png"
                alt="Google Ads"
              />
            </div>
          ),
          meta: {
            defaultVisible: false,
          },
          enableSorting: false,
        },
      ] as ColumnDef<MetricsRow, any>[])
    : []),
  {
    accessorFn: (row) => row.leadGoal,
    id: `Lead Goal`,
    header: `Lead Goal`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `leadGoal`, table.getFilteredRowModel().rows)} type="unitless" />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leads,
    id: `Leads Generated`,
    header: `Leads Generated`,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `leads`, table.getFilteredRowModel().rows)} type="unitless" />
    ),
    meta: {
      ControlledComponent: (row) => (
        <LazyStats
          title="Leads Generated"
          count={row!.leads}
          requestPath={`/api/locations/${
            row!.locationId
          }/lead-statuses?fromDate=${fromDate}&toDate=${toDate}&channel=${channel}`}
          createBreakdownFromResponse={(body: Record<string, string>) =>
            Object.entries(body).map(([key, val]) => ({
              name: key,
              count: Number(val),
              colorHex: getLeadStatusColorMap(key as ContactJson[`leadStatus`]),
            }))
          }
        >
          <MetricCell value={row!.leads} type="unitless" />
        </LazyStats>
      ),
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leadsWithCalls,
    id: `Leads (Phone)`,
    header: `Leads (Phone)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `leadsWithCalls`, table.getFilteredRowModel().rows)} type="unitless" />
    ),
    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.leadsWithChat,
    id: `Leads (Chat)`,
    header: `Leads (Chat)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `leadsWithChat`, table.getFilteredRowModel().rows)} type="unitless" />
    ),
    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.leadsWithForm,
    id: `Leads (Form)`,
    header: `Leads (Form)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `leadsWithForm`, table.getFilteredRowModel().rows)} type="unitless" />
    ),
    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.leadsWithOnlineScheduler,
    id: `Leads (Online Scheduler)`,
    header: `Leads (Online Scheduler)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={summaryTotal(summary, `leadsWithOnlineScheduler`, table.getFilteredRowModel().rows)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.leadsWithVirtualConsultation,
    id: `Leads (Virtual Consultation)`,
    header: `Leads (Virtual Consultation)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={summaryTotal(summary, `leadsWithVirtualConsultation`, table.getFilteredRowModel().rows)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.CPL,
    id: `CPL`,
    header: `CPL`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          summaryTotal(summary, `budget`, table.getFilteredRowModel().rows) /
          summaryTotal(summary, `leads`, table.getFilteredRowModel().rows)
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    id: `CPL (Legacy)`,
    header: `CPL (Legacy)`,
    cell: (props) => (
      <MetricCell
        value={props.row.original.budget / (props.row.original.leads - props.row.original.leadsDisqualified)}
        type="dollars"
      />
    ),
    footer: ({ table }) => (
      <MetricCell
        value={
          summaryTotal(summary, `budget`, table.getFilteredRowModel().rows) /
          (summaryTotal(summary, `leads`, table.getFilteredRowModel().rows) -
            summaryTotal(summary, `leadsDisqualified`, table.getFilteredRowModel().rows))
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.MQLs,
    id: `Qualified Leads Generated`,
    header: `Qualified Leads Generated`,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `MQLs`, table.getFilteredRowModel().rows)} type="unitless" />
    ),
    meta: {
      defaultVisible: true,
      ControlledComponent: (row) => (
        <LazyStats
          title="Qualified Leads Generated"
          count={row!.MQLs}
          requestPath={`/api/locations/${
            row!.locationId
          }/qualified-lead-counts?fromDate=${fromDate}&toDate=${toDate}&channel=${channel}`}
          createBreakdownFromResponse={(body: Record<string, string>) =>
            Object.entries(body).map(([key, val]) => ({
              name: key,
              count: Number(val),
              colorHex: getActivityTypeColorMap(key as ActivityJson[`type`]),
            }))
          }
        >
          <MetricCell value={row!.MQLs} type="unitless" />
        </LazyStats>
      ),
    },
  },
  {
    accessorFn: (row) => row.CPQL,
    id: `CPQL`,
    header: `CPQL`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          summaryTotal(summary, `budget`, table.getFilteredRowModel().rows) /
          summaryTotal(summary, `MQLs`, table.getFilteredRowModel().rows)
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.appointments,
    id: `Appointments Generated`,
    header: `Appointments Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `appointments`, table.getFilteredRowModel().rows)} type="unitless" />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CPA,
    id: `CPA`,
    header: `CPA`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          summaryTotal(summary, `budget`, table.getFilteredRowModel().rows) /
          summaryTotal(summary, `appointments`, table.getFilteredRowModel().rows)
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.patients,
    id: `New Patients Generated`,
    header: `New Patients Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `patients`, table.getFilteredRowModel().rows)} type="unitless" />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CAC,
    id: `CAC`,
    header: `CAC`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          summaryTotal(summary, `budget`, table.getFilteredRowModel().rows) /
          summaryTotal(summary, `patients`, table.getFilteredRowModel().rows)
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    id: `Production`,
    header: `Net Production`,
    footer: ({ table }) => (
      <Stats
        title="Total Net Production"
        unitType="dollars"
        count={
          summaryTotal(summary, `production`, table.getFilteredRowModel().rows) +
          summaryTotal(summary, `scheduledProduction`, table.getFilteredRowModel().rows)
        }
        breakdown={[
          {
            name: `Completed Production`,
            count: summaryTotal(summary, `production`, table.getFilteredRowModel().rows),
          },
          {
            name: `Scheduled Production`,
            count: summaryTotal(summary, `scheduledProduction`, table.getFilteredRowModel().rows),
          },
        ]}
      >
        <MetricCell
          value={
            summaryTotal(summary, `production`, table.getFilteredRowModel().rows) +
            summaryTotal(summary, `scheduledProduction`, table.getFilteredRowModel().rows)
          }
          type="dollars"
        />
      </Stats>
    ),
    meta: {
      ControlledComponent: (row) => (
        <Stats
          title="Net Production"
          unitType="dollars"
          count={row!.production + row!.scheduledProduction}
          breakdown={[
            { name: `Completed Production`, count: row!.production },
            { name: `Scheduled Production`, count: row!.scheduledProduction },
          ]}
        >
          <MetricCell value={row!.production + row!.scheduledProduction} type="dollars" />
        </Stats>
      ),
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.ROI,
    id: `ROI`,
    header: `ROI`,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          summary && typeof summary.ROI === `number`
            ? summary.ROI
            : ((table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.production, 0) +
                table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.scheduledProduction, 0) -
                table
                  .getFilteredRowModel()
                  .rows.reduce(
                    (total, row) =>
                      total +
                      (isSMC
                        ? row.original.budget
                        : (row.original.facebookSpend || 0) + (row.original.googleSpend || 0)),
                    0,
                  )) /
                table
                  .getFilteredRowModel()
                  .rows.reduce(
                    (total, row) =>
                      total +
                      (isSMC
                        ? row.original.budget
                        : (row.original.facebookSpend || 0) + (row.original.googleSpend || 0)),
                    0,
                  )) *
              100
        }
        type="percentage"
      />
    ),
    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.scheduleRate,
    id: `Schedule Rate`,
    header: `Schedule Rate`,
    footer: ({ table }) => (
      <MetricCell
        value={
          summary && typeof summary.scheduleRate === `number`
            ? summary.scheduleRate
            : (table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.appointments ?? 0), 0) /
                table
                  .getFilteredRowModel()
                  .rows.reduce((total, row) => total + (row.original?.qualifiedLeads ?? 0), 0)) *
              100
        }
        type="percentage"
      />
    ),
    meta: {
      ControlledComponent: (row) => (
        <div className="flex w-full items-center gap-x-3 px-2">
          <ProgressBar progress={row!.scheduleRate} />
          <span className="text-body-xs">
            <MetricCell value={row!.scheduleRate} type="percentage" />
          </span>
        </div>
      ),
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.callScheduleRate,
    id: `Call Schedule Rate`,
    header: `Call Schedule Rate`,
    footer: ({ table }) => (
      <MetricCell
        value={
          (table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + (row.original?.scheduledLeadsWithCalls ?? 0), 0) /
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + (row.original?.activeLeadsWithCalls ?? 0), 0)) *
          100
        }
        type="percentage"
      />
    ),
    meta: {
      ControlledComponent: (row) => (
        <div className="flex items-center gap-x-3 px-2">
          <ProgressBar progress={row!.callScheduleRate} />
          <span className="text-body-xs">
            <MetricCell value={row!.callScheduleRate} type="percentage" />
          </span>
        </div>
      ),
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.leadsWithMissedCall + row.leadsWithVoicemailCall,
    id: `Missed Calls`,
    header: `Missed Calls`,
    cell: (props) => (
      <Stats
        title="Missed Calls"
        unitType="unitless"
        count={props.getValue()}
        breakdown={[
          { name: `Missed Calls`, count: props.row.original.leadsWithMissedCall },
          { name: `Voicemail Calls`, count: props.row.original.leadsWithVoicemailCall },
        ]}
      >
        <MetricCell value={props.getValue()} type="unitless" />
      </Stats>
    ),
    footer: ({ table }) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce(
            (total, row) => total + row.original.leadsWithMissedCall + row.original.leadsWithVoicemailCall,
            0,
          )}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.leadsNeedsAction,
    id: `Needs Action`,
    header: `Needs Action`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell value={summaryTotal(summary, `leadsNeedsAction`, table.getFilteredRowModel().rows)} type="unitless" />
    ),
    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.leadsDisqualified,
    id: `Disqualified`,
    header: `Disqualified`,
    footer: ({ table }) => (
      <MetricCell
        value={summaryTotal(summary, `leadsDisqualified`, table.getFilteredRowModel().rows)}
        type="unitless"
      />
    ),
    meta: {
      ControlledComponent: (row) => (
        <LazyStats
          title="Disqualified"
          count={row!.leadsDisqualified}
          requestPath={`/api/locations/${
            row!.locationId
          }/disqualified-reason?fromDate=${fromDate}&toDate=${toDate}&channel=${channel}`}
          createBreakdownFromResponse={(body: Record<string, string>) => {
            return Object.entries(body).map(([key, val]) => ({
              name: key,
              count: Number(val),
            }))
          }}
        >
          <MetricCell value={row!.leadsDisqualified} type="unitless" />
        </LazyStats>
      ),
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.callsInQueue,
    id: `Calls In Queue`,
    header: `Calls In Queue`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.callsInQueue, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
]

export const detailColumns = (
  showSensitiveColumns: boolean,
  isSMC: boolean,
  locationId: string,
  onRowPatch?: MetricsRowPatcher,
): ColumnDef<MetricsRow, any>[] => [
  {
    accessorFn: (row) => row.bucket,
    id: `Date`,
    header: `Date`,
    cell: (props) => formatDate({ value: props.getValue(), format: `MMMM YYYY` }),
    footer: `Total`,
    meta: {
      defaultVisible: true,
      pinned: true,
    },
    enableHiding: false,
  },
  {
    accessorFn: (row) => row.budget,
    id: `Budget`,
    header: `Budget`,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0)}
        type="dollars"
      />
    ),
    meta: {
      ControlledComponent: (row, col, index) => {
        const [originalValue, setOriginalValue] = useState(row!.budget?.toString())
        const [saving, setSaving] = useState(false)
        const handleSave = async (value?: string) => {
          setSaving(true)
          try {
            const budgets = await gqlFetchList<{ id: string; yearMonth: string; channel: string }>(
              'budgets',
              { search: `locationId:${locationId} yearMonth:${row!.bucket!} channel:Digital Marketing` },
              'id yearMonth channel',
            )
            const budget = budgets.hits[0]
            if (budget) {
              const res = await gqlMutation(UPDATE_BUDGET_MUTATION, { id: budget.id, input: { amount: Number(value) } })
              if (res.error) throw new Error(res.error.message)
              notifySuccess(`Budget updated`)
            } else {
              const res = await gqlMutation(CREATE_BUDGET_MUTATION, {
                input: { locationId, yearMonth: row!.bucket!, channel: 'Digital Marketing', amount: Number(value) },
              })
              if (res.error) throw new Error(res.error.message)
              notifySuccess(`Budget updated`)
            }
            onRowPatch?.(row!, { budget: Number(value) })
            setOriginalValue(value!)
          } catch (err) {
            logError(`Failed to update budget`, err)
            notifyError(`Failed to update budget`)
          } finally {
            setSaving(false)
          }
        }
        return <EditableCell originalValue={originalValue} type="dollars" onSave={handleSave} />
      },
      defaultVisible: true,
    },
  },
  ...(showSensitiveColumns
    ? ([
        {
          accessorFn: (row) => (row.facebookSpend || 0) + (row.googleSpend || 0),
          id: `Total Spend`,
          header: `Total Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={table
                .getFilteredRowModel()
                .rows.reduce(
                  (total, row) => total + (row.original.facebookSpend || 0) + (row.original.googleSpend || 0),
                  0,
                )}
              type="dollars"
            />
          ),
          meta: {
            defaultVisible: false,
          },
        },
        {
          accessorFn: (row) => row.googleSpend,
          id: `Google Spend`,
          header: `Google Spend`,
          cell: ({ row }) => {
            let lsa = row.original.lsaSpend || 0
            let total = row.original.googleSpend || 0
            let googleOnly = total - lsa
            if (lsa > 0) {
              return (
                <Tooltip
                  message={
                    <div className="text-xs">
                      <div>
                        Google Ads: $
                        {googleOnly.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                      <div>
                        Local Service Ads: $
                        {lsa.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  }
                >
                  <MetricCell value={total} type="dollars" />
                </Tooltip>
              )
            }
            return <MetricCell value={total} type="dollars" />
          },
          footer: ({ table }) => (
            <MetricCell
              value={table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + (row.original.googleSpend || 0), 0)}
              type="dollars"
            />
          ),
          meta: {
            defaultVisible: false,
          },
        },
        {
          accessorFn: (row) => row.facebookSpend,
          id: `Facebook Spend`,
          header: `Facebook Spend`,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + (row.original.facebookSpend || 0), 0)}
              type="dollars"
            />
          ),
          meta: {
            defaultVisible: false,
          },
        },
        {
          accessorFn: (row) => row.targetSpend,
          id: `Target Spend`,
          header: `Target Spend`,
          footer: ({ table }) => (
            <MetricCell
              value={table
                .getFilteredRowModel()
                .rows.reduce((total, row) => total + (row.original.targetSpend || 0), 0)}
              type="dollars"
            />
          ),
          meta: {
            ControlledComponent: (row, col, index) => {
              const [originalValue, setOriginalValue] = useState(row!.targetSpend?.toString())
              const [saving, setSaving] = useState(false)
              const handleSave = async (value?: string) => {
                setSaving(true)
                try {
                  const budgets = await gqlFetchList<{ id: string }>(
                    'budgets',
                    { search: `locationId:${locationId} yearMonth:${row!.bucket!} channel:Digital Marketing` },
                    'id',
                  )
                  const budget = budgets.hits[0]
                  if (budget) {
                    const res = await gqlMutation(UPDATE_BUDGET_MUTATION, {
                      id: budget.id,
                      input: { targetSpend: Number(value) },
                    })
                    if (res.error) throw new Error(res.error.message)
                    notifySuccess(`Target Spend updated`)
                  } else {
                    const res = await gqlMutation(CREATE_BUDGET_MUTATION, {
                      input: {
                        locationId,
                        yearMonth: row!.bucket!,
                        channel: 'Digital Marketing',
                        targetSpend: Number(value),
                      },
                    })
                    if (res.error) throw new Error(res.error.message)
                    notifySuccess(`Target Spend updated`)
                  }
                  onRowPatch?.(row!, { targetSpend: Number(value) })
                  setOriginalValue(value!)
                } catch (err) {
                  logError(`Failed to update Target Spend`, err)
                  notifyError(`Failed to update Target Spend`)
                } finally {
                  setSaving(false)
                }
              }
              return <EditableCell originalValue={originalValue} type="dollars" onSave={handleSave} />
            },
            defaultVisible: false,
          },
        },
        {
          id: `iCPL`,
          header: `iCPL`,
          accessorFn: (row) => row.iCPL,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={
                table
                  .getFilteredRowModel()
                  .rows.reduce(
                    (total, row) =>
                      row.original.facebookSpend != null && row.original.googleSpend != null
                        ? total + row.original.facebookSpend + row.original.googleSpend
                        : 0,
                    0,
                  ) / table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leads, 0)
              }
              type="dollars"
            />
          ),

          meta: {},
        },
        {
          id: `iCPQL`,
          header: `iCPQL`,
          accessorFn: (row) => row.iCPQL,
          cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
          footer: ({ table }) => (
            <MetricCell
              value={
                table
                  .getFilteredRowModel()
                  .rows.reduce(
                    (total, row) =>
                      row.original.facebookSpend != null && row.original.googleSpend != null
                        ? total + row.original.facebookSpend + row.original.googleSpend
                        : 0,
                    0,
                  ) / table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.MQLs, 0)
              }
              type="dollars"
            />
          ),

          meta: {},
        },
      ] as ColumnDef<MetricsRow, any>[])
    : []),
  {
    accessorFn: (row) => row.leadGoal,
    id: `Lead Goal`,
    header: `Lead Goal`,
    // cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadGoal, 0)}
        type="unitless"
      />
    ),
    meta: {
      ControlledComponent: (row, col, index) => {
        const [originalValue, setOriginalValue] = useState(row!.leadGoal.toString())
        const [saving, setSaving] = useState(false)
        const handleSave = async (value?: string) => {
          setSaving(true)
          try {
            const budgets = await gqlFetchList<{ id: string }>(
              'budgets',
              { search: `locationId:${locationId} yearMonth:${row!.bucket!} channel:Digital Marketing` },
              'id',
            )
            const budget = budgets.hits[0]
            if (budget) {
              const res = await gqlMutation(UPDATE_BUDGET_MUTATION, {
                id: budget.id,
                input: { leadGoal: Number(value) },
              })
              if (res.error) throw new Error(res.error.message)
              notifySuccess(`Lead Goal updated`)
            } else {
              const res = await gqlMutation(CREATE_BUDGET_MUTATION, {
                input: { locationId, yearMonth: row!.bucket!, channel: 'Digital Marketing', leadGoal: Number(value) },
              })
              if (res.error) throw new Error(res.error.message)
              notifySuccess(`Lead Goal updated`)
            }
            onRowPatch?.(row!, { leadGoal: Number(value) })
            setOriginalValue(value!)
          } catch (err) {
            logError(`Failed to update Lead Goal`, err)
            notifyError(`Failed to update Lead Goal`)
          } finally {
            setSaving(false)
          }
        }
        return <EditableCell originalValue={originalValue} type="unitless" onSave={handleSave} />
      },
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leads,
    id: `Leads Generated`,
    header: `Leads Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leads, 0)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leadsWithCalls,
    id: `Leads (Phone)`,
    header: `Leads (Phone)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsWithCalls, 0)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leadsWithChat,
    id: `Leads (Chat)`,
    header: `Leads (Chat)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsWithChat, 0)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leadsWithForm,
    id: `Leads (Form)`,
    header: `Leads (Form)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsWithForm, 0)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leadsWithOnlineScheduler,
    id: `Leads (Online Scheduler)`,
    header: `Leads (Online Scheduler)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + row.original.leadsWithOnlineScheduler, 0)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.leadsWithVirtualConsultation,
    id: `Leads (Virtual Consultation)`,
    header: `Leads (Virtual Consultation)`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table
          .getFilteredRowModel()
          .rows.reduce((total, row) => total + row.original.leadsWithVirtualConsultation, 0)}
        type="unitless"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CPL,
    id: `CPL`,
    header: `CPL`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leads, 0)
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    id: `CPL (Legacy)`,
    header: `CPL (Legacy)`,
    cell: (props) => (
      <MetricCell
        value={props.row.original.budget / (props.row.original.leads - props.row.original.leadsDisqualified)}
        type="dollars"
      />
    ),
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + row.original.leads - row.original.leadsDisqualified, 0)
        }
        type="dollars"
      />
    ),

    meta: {},
  },
  {
    accessorFn: (row) => row.MQLs,
    id: `Qualified Leads Generated`,
    header: `Qualified Leads Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.MQLs, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CPQL,
    id: `CPQL`,
    header: `CPQL`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.MQLs, 0)
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.appointments,
    id: `Appointments Generated`,
    header: `Appointments Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.appointments, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CPA,
    id: `CPA`,
    header: `CPA`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.appointments, 0)
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.patients,
    id: `New Patients Generated`,
    header: `New Patients Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.patients, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.CAC,
    id: `CAC`,
    header: `CAC`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.budget, 0) /
          table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.patients, 0)
        }
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.production,
    id: `Total Production Generated`,
    header: `Total Production Generated`,
    cell: (props) => <MetricCell value={props.getValue()} type="dollars" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.production, 0)}
        type="dollars"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.ROI,
    id: `ROI`,
    header: `ROI`,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    meta: {
      defaultVisible: false,
    },
    footer: ({ table }) => (
      <MetricCell
        value={
          ((table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.production, 0) +
            table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.scheduledProduction, 0) -
            table
              .getFilteredRowModel()
              .rows.reduce(
                (total, row) =>
                  total +
                  (isSMC ? row.original.budget : (row.original.facebookSpend || 0) + (row.original.googleSpend || 0)),
                0,
              )) /
            table
              .getFilteredRowModel()
              .rows.reduce(
                (total, row) =>
                  total +
                  (isSMC ? row.original.budget : (row.original.facebookSpend || 0) + (row.original.googleSpend || 0)),
                0,
              )) *
          100
        }
        type="percentage"
      />
    ),
  },
  {
    accessorFn: (row) => row.scheduleRate,
    id: `Schedule Rate`,
    header: `Schedule Rate`,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    footer: ({ table }) => (
      <MetricCell
        value={Math.floor(
          (table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.appointments ?? 0), 0) /
            table.getFilteredRowModel().rows.reduce((total, row) => total + (row.original?.qualifiedLeads ?? 0), 0)) *
            100,
        )}
        type="percentage"
      />
    ),
    meta: {
      defaultVisible: true,
    },
  },
  {
    accessorFn: (row) => row.callScheduleRate,
    id: `Phone Schedule Rate`,
    header: `Phone Schedule Rate`,
    cell: (props) => <MetricCell value={props.getValue()} type="percentage" />,
    footer: ({ table }) => (
      <MetricCell
        value={Math.floor(
          (table
            .getFilteredRowModel()
            .rows.reduce((total, row) => total + (row.original?.scheduledLeadsWithCalls ?? 0), 0) /
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + (row.original?.activeLeadsWithCalls ?? 0), 0)) *
            100,
        )}
        type="percentage"
      />
    ),
    meta: {},
  },
  // {
  //   accessorFn: (row) => row.leadsWithMissedCall,
  //   id: `Missed Calls`,
  //   header: `Missed Calls`,
  //   cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
  //   footer: ({ table }) => (
  //     <MetricCell
  //       value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsWithMissedCall, 0)}
  //       type="unitless"
  //     />
  //   ),

  //   meta: {
  //     defaultVisible: true,
  //   },
  // },
  {
    accessorFn: (row) => row.leadsDisqualified,
    id: `Disqualified`,
    header: `Disqualified`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.leadsDisqualified, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
  {
    accessorFn: (row) => row.callsInQueue,
    id: `Calls In Queue`,
    header: `Calls In Queue`,
    cell: (props) => <MetricCell value={props.getValue()} type="unitless" />,
    footer: ({ table }) => (
      <MetricCell
        value={table.getFilteredRowModel().rows.reduce((total, row) => total + row.original.callsInQueue, 0)}
        type="unitless"
      />
    ),

    meta: {
      defaultVisible: false,
    },
  },
]

export const filterableColumns = (
  showSensitiveColumns: boolean,
  preAppliedFilters: PredefinedFilter,
): FilterableColumn[] => {
  const filters = [
    {
      attribute: `date`,
      displayName: `Date`,
      type: FilterValueType.rangeDate,
      customSearchParam: `date`,
      limitToMonth: showSensitiveColumns ? false : true,
      includeWeeks: true,
      includeToday: true,
      includeYesterday: true,
    },
    {
      attribute: `location.id`,
      displayName: `Location`,
      optionsSource: `/api/locations`,
      uniqOperator: `in`,
      type: FilterValueType.id,
    },
    {
      attribute: `workspace.id`,
      displayName: `Workspaces`,
      type: FilterValueType.id,
      fetcher: (params) => gqlFetchList('workspaces', { search: params.query }, 'id name'),
    },
    {
      attribute: `location.orgTags`,
      displayName: `Org Tags`,
      type: FilterValueType.stringArray,
      optionsSource: `/api/org-tags/locations`,
    },
    {
      attribute: `location.pmsIntegrated`,
      displayName: `PMS Integration`,
      type: FilterValueType.pmsIntegration,
      options: [
        { id: `Active`, value: `true`, name: `Active` },
        { id: `Inactive`, value: `false`, name: `Inactive` },
      ],
    },
    {
      attribute: `budget`,
      displayName: `Budget`,
      type: FilterValueType.number,
    },
    ...(showSensitiveColumns
      ? ([
          {
            attribute: `facebookSpend`,
            displayName: `Facebook Spend`,
            type: FilterValueType.number,
          },
          {
            attribute: `googleSpend`,
            displayName: `Google Spend`,
            type: FilterValueType.number,
          },
          {
            attribute: `targetSpend`,
            displayName: `Target Spend`,
            type: FilterValueType.number,
          },
          {
            attribute: `location.facebookAdsActive`,
            displayName: `Facebook Ads`,
            type: FilterValueType.boolean,
            uniqOperator: `exactly` as FilterOperator,
            options: [
              { id: `Active`, value: `true`, name: `Active` },
              { id: `Inactive`, value: `false`, name: `Inactive` },
            ],
          },
          {
            attribute: `location.googleAdsActive`,
            displayName: `Google Ads`,
            type: FilterValueType.boolean,
            uniqOperator: `exactly` as FilterOperator,
            options: [
              { id: `Active`, value: `true`, name: `Active` },
              { id: `Inactive`, value: `false`, name: `Inactive` },
            ],
          },
        ] as FilterableColumn[])
      : []),
    {
      attribute: `workspaceId`,
      secondaryAttribute: `assignedTo`,
      displayName: `Assigned To`,
      uniqOperator: `in`,
      type: FilterValueType.assignedTo,
    },
    {
      attribute: `leadGoal`,
      displayName: `Lead Goal`,
      type: FilterValueType.number,
    },
    {
      attribute: `leads`,
      displayName: `Leads Generated`,
      type: FilterValueType.number,
    },
    {
      attribute: `CPL`,
      displayName: `CPL`,
      type: FilterValueType.number,
    },
    {
      attribute: `MQLs`,
      displayName: `Qualified Leads Generated`,
      type: FilterValueType.number,
    },
    {
      attribute: `CPQL`,

      displayName: `CPQL`,
      type: FilterValueType.number,
    },
    {
      attribute: `appointments`,
      displayName: `Appointments Generated`,
      type: FilterValueType.number,
    },
    {
      attribute: `CPA`,
      displayName: `CPA`,
      type: FilterValueType.number,
    },
    {
      attribute: `patients`,
      displayName: `New Patients Generated`,
      type: FilterValueType.number,
    },
    {
      attribute: `CAC`,
      displayName: `CAC`,
      type: FilterValueType.number,
    },
    {
      attribute: `production`,
      displayName: `Total Production Generated`,
      type: FilterValueType.number,
    },
    {
      attribute: `ROI`,
      displayName: `ROI`,
      type: FilterValueType.number,
    },
    {
      attribute: `conversionRate`,
      displayName: `Conversion Rate`,
      type: FilterValueType.number,
    },
    {
      attribute: `leadsWithMissedCall`,
      displayName: `Missed Calls`,
      type: FilterValueType.number,
    },
    {
      attribute: `leadsNeedsAction`,
      displayName: `Needs Action`,
      type: FilterValueType.number,
    },
    {
      attribute: `callsInsuranceIssues`,
      displayName: `Insurance Issues`,
      type: FilterValueType.number,
    },
    {
      attribute: `callsInQueue`,
      displayName: `Calls In Queue`,
      type: FilterValueType.number,
    },
  ] as FilterableColumn[]
  return filters?.map((filter) => ({ ...filter, ...addDefaultFilterProperties(filter.attribute, preAppliedFilters) }))
}
